import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { ArrowRight as ArrowRightIcon } from '@mui/icons-material';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import dayjs from 'dayjs';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateContract, useGetContract, useUpdateContract } from '../../hooks/api/use-contracts.hook';
import { ContractApiModel } from '../../../../types/models/contract.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { ContractForm } from './ContractForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditContract = () => {
  const navigate = useNavigate();
  const { contractId } = useParams<{ contractId: string }>();
  const [showContractor, setShowContractor] = useState(true);
  const [showResidentEngineer, setShowResidentEngineer] = useState(true);

  const {
    contract,
    loading: loadingContract,
    error: gettingError,
  } = useGetContract<ContractApiModel>({ contractId: Number(contractId) });
  const [createContract, { loading: creatingContract, error: createError }] = useCreateContract();
  const [updateContract, { loading: updatingContract, error: updateError }] = useUpdateContract();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<ContractForm>({ resolver: classValidatorResolver(ContractForm, {}), values: contract });

  const onSubmit: SubmitHandler<ContractForm> = async (submittedValues) => {
    const updatedValues = getDirtyValues(dirtyFields, submittedValues);
    await (contract
      ? updateContract({ ...updatedValues, contractId: contract.id })
      : createContract({ ...submittedValues }));
    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    const defaultTextFieldParams: TextFieldProps = {
      variant: 'outlined',
      sx: { mt: '1rem' },
      disabled: creatingContract || updatingContract,
      margin: 'dense',
      size: 'small',
      fullWidth: true,
    };

    return (
      <>
        <TextField
          {...register('number', { valueAsNumber: true })}
          {...defaultTextFieldParams}
          error={Boolean(errors.number)}
          helperText={errors.number?.message}
          label="Number"
        />
        <TextField
          {...register('district', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.district)}
          helperText={errors.district?.message}
          label="District"
        />
        <TextField
          {...register('ea', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.ea)}
          helperText={errors.ea?.message}
          label="EA"
        />
        <TextField
          {...register('county', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.ea)}
          helperText={errors.ea?.message}
          label="County"
        />
        <TextField
          {...register('pmBackPreCityCr', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.pmBackPreCityCr)}
          helperText={errors.pmBackPreCityCr?.message}
          label="Pm. Back Pre City Cr."
        />
        <TextField
          {...register('descriptionOfWork', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.descriptionOfWork)}
          helperText={errors.descriptionOfWork?.message}
          label="Work description"
        />
        <TextField
          {...register('locationDescription', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.locationDescription)}
          helperText={errors.locationDescription?.message}
          label="Location description"
        />
        <TextField
          {...register('federalAidLocation', { setValueAs: (value) => value || null })}
          {...defaultTextFieldParams}
          error={Boolean(errors.federalAidLocation)}
          helperText={errors.federalAidLocation?.message}
          label="Federal Aid Location"
        />
        <Controller
          control={control}
          name="bidOpenDate"
          render={({ field }) => (
            <DatePicker
              {...register('bidOpenDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Bid Open Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.bidOpenDate),
                  helperText: errors.bidOpenDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="awardDate"
          render={({ field }) => (
            <DatePicker
              {...register('awardDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Award Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.awardDate),
                  helperText: errors.awardDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="approvalDate"
          render={({ field }) => (
            <DatePicker
              {...register('approvalDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Approval Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.approvalDate),
                  helperText: errors.approvalDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="workMustStartDate"
          render={({ field }) => (
            <DatePicker
              {...register('workMustStartDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Work Must Start Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.workMustStartDate),
                  helperText: errors.workMustStartDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="estCompDate"
          render={({ field }) => (
            <DatePicker
              {...register('estCompDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Estimated Completion Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.estCompDate),
                  helperText: errors.estCompDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="actualCompDate"
          render={({ field }) => (
            <DatePicker
              {...register('actualCompDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Actual Completion Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.actualCompDate),
                  helperText: errors.actualCompDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="acceptanceDate"
          render={({ field }) => (
            <DatePicker
              {...register('acceptanceDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Acceptance Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.acceptanceDate),
                  helperText: errors.acceptanceDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="suspendDate"
          render={({ field }) => (
            <DatePicker
              {...register('suspendDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Suspend Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.suspendDate),
                  helperText: errors.suspendDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="lastEstDate"
          render={({ field }) => (
            <DatePicker
              {...register('lastEstDate', { setValueAs: (value) => (value ? new Date(value) : null) })}
              label="Last Estimated Date"
              onChange={(date) => field.onChange(date?.toDate())}
              value={field.value ? dayjs(field.value) : null}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { marginTop: '1rem' },
                  fullWidth: true,
                  error: Boolean(errors.lastEstDate),
                  helperText: errors.lastEstDate?.message,
                },
                actionBar: { actions: ['today', 'clear'] },
              }}
            />
          )}
        />
        <Divider sx={{ mt: '1rem', mb: '1rem' }} />
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => setShowContractor(!showContractor)}>
            <ArrowRightIcon />
          </IconButton>
          <Typography variant="h6">Contractor</Typography>
        </Box>
        <Collapse in={showContractor} sx={{ pl: '2.5em' }}>
          <TextField
            {...register('contractor.name', { setValueAs: (value) => value || null })}
            {...defaultTextFieldParams}
            error={Boolean(errors.contractor?.name)}
            helperText={errors.contractor?.name?.message}
            label="Name"
          />
          <TextField
            {...register('contractor.address', { setValueAs: (value) => value || null })}
            {...defaultTextFieldParams}
            error={Boolean(errors.contractor?.address)}
            helperText={errors.contractor?.address?.message}
            label="Address"
          />
          <TextField
            {...register('contractor.city', { setValueAs: (value) => value || null })}
            {...defaultTextFieldParams}
            error={Boolean(errors.contractor?.city)}
            helperText={errors.contractor?.city?.message}
            label="City"
          />
        </Collapse>
        <Divider sx={{ mt: '1rem', mb: '1rem' }} />
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => setShowResidentEngineer(!showResidentEngineer)}>
            <ArrowRightIcon />
          </IconButton>
          <Typography variant="h6">Resident Engineer</Typography>
        </Box>
        <Collapse in={showResidentEngineer} sx={{ pl: '2.5em' }}>
          <TextField
            {...register('residentEngineer.name', { setValueAs: (value) => value || null })}
            {...defaultTextFieldParams}
            error={Boolean(errors.residentEngineer?.name)}
            helperText={errors.residentEngineer?.name?.message}
            label="Name"
          />
          <TextField
            {...register('residentEngineer.phone', { setValueAs: (value) => value || null })}
            {...defaultTextFieldParams}
            error={Boolean(errors.residentEngineer?.phone)}
            helperText={errors.residentEngineer?.phone?.message}
            label="Phone"
          />
        </Collapse>
      </>
    );
  };

  if (loadingContract) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingContract ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${contract ? 'Edit' : 'Add'} Contract`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {contract ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
